import React, { useMemo } from "react";
import { Button, Space, Typography, message } from "antd";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  LinkOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { getMilestoneView } from "../services/orderMilestoneView.js";
import ProviderAvatar from "./ProviderAvatar.jsx";
import { useOrderTrackingLink } from "../hooks/useOrderTrackingLink.js";

const { Text } = Typography;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StageList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StageItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: ${(props) => {
    if (props.$state === "current") return "#1890ff";
    if (props.$state === "completed") return "#52c41a";
    if (props.$state === "problem") return "#ff4d4f";
    return "#8c8c8c";
  }};
  font-weight: ${(props) => (props.$state === "current" ? 600 : 400)};
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => {
    if (props.$state === "current") return "#1890ff";
    if (props.$state === "completed") return "#52c41a";
    if (props.$state === "problem") return "#ff4d4f";
    return "transparent";
  }};
  border: 2px solid
    ${(props) => {
      if (props.$state === "current") return "#1890ff";
      if (props.$state === "completed") return "#52c41a";
      if (props.$state === "problem") return "#ff4d4f";
      return "#d9d9d9";
    }};
`;

const SectionLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8c8c8c;
  margin-bottom: 8px;
  font-weight: 600;
`;

/**
 * Journey / milestone quick-view for the Active Deliveries detail panel.
 * @param {{ order: Object, onContactProvider?: () => void, onUpdateMilestone?: () => void }} props
 */
export default function OrderJourneyPanel({
  order,
  onContactProvider,
  onUpdateMilestone,
}) {
  const view = useMemo(() => getMilestoneView(order), [order]);
  const providerName =
    order?.booking?.assignedTeam?.partnerName ||
    order?.assignedTeam?.partnerName ||
    null;
  const providerPhone =
    order?.booking?.assignedTeam?.primaryDriver?.phone ||
    order?.booking?.assignedTeam?.phone ||
    null;
  const orderRef = order?.id || order?.jobId;
  const { data: trackingLink, isLoading: trackingLinkLoading } =
    useOrderTrackingLink(orderRef, { enabled: Boolean(orderRef) });
  const trackerUrl = trackingLink?.trackingUrl || null;
  const lastUpdated = view.lastUpdatedAt
    ? new Date(view.lastUpdatedAt).toLocaleString()
    : "N/A";

  const handleCopyLink = async () => {
    if (!trackerUrl) {
      message.warning("Tracker link unavailable");
      return;
    }
    try {
      await navigator.clipboard.writeText(trackerUrl);
      message.success("Tracker link copied");
    } catch {
      message.error("Failed to copy tracker link");
    }
  };

  const handleOpenTracker = () => {
    if (!trackerUrl) {
      message.warning("Tracker link unavailable");
      return;
    }
    window.open(trackerUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Panel>
      <div>
        <SectionLabel>Delivery progress</SectionLabel>
        <StageList>
          {view.stages.map((stage) => (
            <StageItem key={stage.key} $state={stage.state}>
              {stage.state === "completed" ? (
                <CheckCircleFilled style={{ color: "#52c41a", fontSize: 14 }} />
              ) : (
                <Dot $state={stage.state} />
              )}
              <span>{stage.title}</span>
            </StageItem>
          ))}
        </StageList>
        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: "block" }}>
          <ClockCircleOutlined /> Last update: {lastUpdated}
        </Text>
      </div>

      <div>
        <SectionLabel>Provider</SectionLabel>
        <ProviderAvatar name={providerName} />
        {providerPhone && (
          <div style={{ marginTop: 8 }}>
            <a href={`tel:${providerPhone}`}>
              <PhoneOutlined /> {providerPhone}
            </a>
          </div>
        )}
      </div>

      <div>
        <SectionLabel>Customer tracker</SectionLabel>
        <Space wrap>
          <Button
            icon={<LinkOutlined />}
            onClick={handleOpenTracker}
            loading={trackingLinkLoading}
          >
            Open Tracker
          </Button>
          <Button onClick={handleCopyLink} loading={trackingLinkLoading}>
            Copy Link
          </Button>
        </Space>
      </div>

      <div>
        <SectionLabel>Internal actions</SectionLabel>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button type="primary" block onClick={onUpdateMilestone} danger>
            Update Milestone
          </Button>
          <Button block onClick={onContactProvider}>
            Contact Provider
          </Button>
        </Space>
      </div>
    </Panel>
  );
}
